import { FilteredItems, ItemValueFunc } from './types'
import type { ChartConfig, ChartValue, GenericChartItem } from 'src/app/types/chart.model'
import { UserDto } from 'src/app/types/dto/user.dto'
import { UtilsService } from '../utils.service'

export class ChartDatasourceHelper {
  static buildItemValueFunc(
    config: ChartConfig,
    itemValueFunc?: ItemValueFunc
  ): ItemValueFunc {
    if (itemValueFunc) return itemValueFunc

    const isCount = UtilsService.chartValueAggregationIsCount(config)
    return (item) => {
      if (isCount) return 1
      if (!(config.valueField in item)) {
        throw new Error(`Value field '${config.valueField}' is missing from chart data`)
      }

      const rawValue = item[config.valueField]
      if (rawValue == null || rawValue === '') return 0

      const value = Number(rawValue)
      if (!Number.isFinite(value)) {
        throw new Error(
          `Value field '${config.valueField}' contains a non-numeric value: ${String(rawValue)}`
        )
      }
      return value
    }
  }

  /** Build a Set of fullNames for inactive users */
  static buildInactiveNameSet(users: UserDto[]): Set<string> {
    return new Set(users.filter(u => !u.isActive).map(u => u.fullName))
  }

  /** Build a 0-based rank map (value -> rank) from an ordered list of keys.
   *  Used to give a field a custom display order (instead of alphabetical) on chart
   *  argument axes / split-by series. Passed explicitly as a `FieldRankMaps` entry
   *  (see chart-datasource-provider.service.ts) */
  static buildRankMap(orderedKeys: string[]): Map<string, number> {
    const map = new Map<string, number>()
    orderedKeys.forEach((key, index) => map.set(key, index))
    return map
  }

  /** Remove items where any of the given person fields matches an inactive user name */
  static removeInactiveItems(items: any[], inactiveNames: Set<string>, personFields: string[]): any[] {
    return items.filter(item =>
      !personFields.some(field => inactiveNames.has(item[field]))
    )
  }

  /** Apply inactive-user filtering to a FilteredItems wrapper */
  static applyInactiveFilter(filteredItems: FilteredItems, inactiveNames: Set<string>, personFields: string[]): FilteredItems {
    const kept = ChartDatasourceHelper.removeInactiveItems(filteredItems.items, inactiveNames, personFields)
    return {
      ...filteredItems,
      items: kept,
      filteredIds: filteredItems.filteredIds.filter(id =>
        kept.some((item: any) => item.id === id)
      ),
    }
  }
  /**
   * Adds target values to each data point based on targetOverrides or default targetValue.
   * @returns true if dynamic targets are present (from targetOverrides)
   */
  static buildTargetData(data: GenericChartItem[], config: ChartConfig): boolean {
    const overrides = config.targetOverrides
    const defaultTarget = config.targetValue

    // Check if we have any overrides - if so, add target values to data points
    if (overrides && Object.keys(overrides).length > 0) {
      // Add target value to each data point
      for (const item of data) {
        const groupField = item.groupField
        // Generate key based on groupField type (date or category string)
        const key = String(groupField)
        const targetValue = overrides[key] ?? defaultTarget

        // Add the target value to the data item for the target series
        item.target = targetValue ?? null
      }
      return true
    }

    return false
  }

  static convertAndOrderDates(items: any[], dateField: string): void {
    items.forEach((i) => {
      const dateString = i[dateField]
      if (dateString) {
        i[dateField] = new Date(i[dateField])
      }
    })

    items.sort(
      (a, b) =>
        (Object.prototype.hasOwnProperty.call(a, dateField) && a[dateField]
          ? a[dateField].getTime()
          : 0) -
        (Object.prototype.hasOwnProperty.call(b, dateField) && b[dateField]
          ? b[dateField].getTime()
          : 0)
    )
  }

  static initChartItemZeroValues(item: GenericChartItem, chartSeries: ChartValue[]): void {
    const seriesNumItems: { [key: string]: number } = {}
    chartSeries.forEach((s) => (seriesNumItems[s.value] = 0))

    item['seriesNumItems'] = seriesNumItems

    chartSeries.forEach((s) => (item[s.value] = 0))
  }
}
